import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PortalPageHeader from './PortalPageHeader';
import PortalStatusPill from './PortalStatusPill';
import QuickViewDrawer from '../sub-components/QuickViewDrawer';
import { portalCardSx, statusColor } from './portalStyles';
import PortalQuoteService from '../../services/PortalQuoteService';

const STATUS_LABELS = {
    SENT: 'Sent',
    AWAITING_CUSTOMER_SIGNATURE: 'Awaiting Signature',
    AWAITING_SIGNATURE: 'Awaiting Signature',
    CHANGES_REQUESTED: 'Changes Requested',
    SIGNED: 'Signed',
    ACCEPTED: 'Accepted',
    REJECTED: 'Declined',
    EXPIRED: 'Expired',
    CANCELLED: 'Cancelled',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const fmtAmount = (v) => v != null ? `CHF ${Number(v).toFixed(2)}` : '—';

export default function PortalQuotesPage() {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [quickView, setQuickView] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await PortalQuoteService.getQuotes();
                setQuotes(data || []);
            } catch {
                // error handled silently — empty state shows
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    return (
        <PortalLayout>
            <PortalPageHeader title="Quotes" subtitle="Review and respond to quotes sent to you" />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            ) : quotes.length === 0 ? (
                <Paper elevation={0} sx={{ ...portalCardSx, p: 6, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#64748b' }}>
                        No quotes available
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                        Quotes shared with you will appear here.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={portalCardSx}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quotes.map((q) => (
                                <TableRow
                                    key={q.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => setQuickView(q)}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                            {q.displayName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <PortalStatusPill status={q.status} label={STATUS_LABELS[q.status]} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: '#475569' }}>{fmtDate(q.quoteDate)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1a2332' }}>
                                            {fmtAmount(q.totalAmount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <ChevronRightIcon sx={{ color: '#cbd5e1', display: 'block' }} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Quick-view drawer — PDF preview + key facts */}
            <QuickViewDrawer
                open={Boolean(quickView)}
                onClose={() => setQuickView(null)}
                title={quickView?.displayName}
                statusColor={statusColor(quickView?.status)}
                statusLabel={quickView ? (STATUS_LABELS[quickView.status] || quickView.status) : null}
                facts={quickView ? [
                    { label: 'Date', value: fmtDate(quickView.quoteDate) },
                    { label: 'Amount', value: fmtAmount(quickView.totalAmount) },
                ] : []}
                loadPdf={() => PortalQuoteService.downloadPdf(quickView.id)}
                onOpenFull={() => navigate(`/portal/quotes/${quickView.id}`)}
                openLabel="Open quote"
                pdfErrorLabel="PDF preview not available"
            />
        </PortalLayout>
    );
}
